import * as THREE from 'three';

type Kind = 'color' | 'data';
type Entry = { lo: THREE.Texture; hi?: THREE.Texture; hiLoading: boolean; kind: Kind; ext: string };

/**
 * Two-tier planet textures: 2K ("lo") always resident, full resolution ("hi", up to 8K)
 * streamed in when the camera is near the body and released when it leaves.
 */
export class TextureManager {
  private readonly loader = new THREE.TextureLoader();
  private readonly entries = new Map<string, Entry>();
  private readonly listeners = new Set<(key: string) => void>();
  private pending = 0;

  constructor(private readonly renderer: THREE.WebGLRenderer) {}

  get loading(): number {
    return this.pending;
  }

  onChange(fn: (key: string) => void): void {
    this.listeners.add(fn);
  }

  /** Resolves when the 2K tier is ready. */
  async preload(key: string, kind: Kind = 'color', ext = 'jpg'): Promise<THREE.Texture> {
    const existing = this.entries.get(key);
    if (existing) return existing.lo;
    const lo = await this.load(`textures/lo/${key}.${ext}`, kind);
    this.entries.set(key, { lo, hiLoading: false, kind, ext });
    return lo;
  }

  get(key: string): THREE.Texture | null {
    const e = this.entries.get(key);
    if (!e) return null;
    return e.hi ?? e.lo;
  }

  isHi(key: string): boolean {
    return !!this.entries.get(key)?.hi;
  }

  requestHi(key: string): void {
    const e = this.entries.get(key);
    if (!e || e.hi || e.hiLoading) return;
    e.hiLoading = true;
    this.load(`textures/hi/${key}.${e.ext}`, e.kind)
      .then((tex) => {
        e.hi = tex;
        e.hiLoading = false;
        this.listeners.forEach((fn) => fn(key));
      })
      .catch(() => {
        e.hiLoading = false;
      });
  }

  releaseHi(key: string): void {
    const e = this.entries.get(key);
    if (!e?.hi) return;
    e.hi.dispose();
    e.hi = undefined;
    this.listeners.forEach((fn) => fn(key));
  }

  private load(url: string, kind: Kind): Promise<THREE.Texture> {
    this.pending++;
    return new Promise<THREE.Texture>((resolve, reject) => {
      this.loader.load(
        url,
        (tex) => {
          tex.colorSpace = kind === 'color' ? THREE.SRGBColorSpace : THREE.NoColorSpace;
          tex.anisotropy = this.renderer.capabilities.getMaxAnisotropy();
          tex.wrapS = THREE.RepeatWrapping;
          tex.minFilter = THREE.LinearMipmapLinearFilter;
          tex.generateMipmaps = true;
          this.pending--;
          resolve(tex);
        },
        undefined,
        (err) => {
          this.pending--;
          reject(err);
        },
      );
    });
  }
}
